// 📄 services/recentesLocalService.ts
//
// "Accesos rápidos" en el Home (2026-08-20, a pedido del usuario: llegar a
// un proyecto puntual toma varios pasos — Mis Proyectos → buscarlo →
// entrar). Recientes y fijados son puramente UX/conveniencia por
// dispositivo, no datos de negocio — se guardan en localStorage, NO en
// Firestore (no hace falta sincronizar entre dispositivos, ni reglas
// nuevas, ni un trigger). Mismo criterio ya usado para `orgActiva` (ver
// AuthContext.tsx, Sub-fase G1).
//
// Clave por usuario (uid) para no mezclar el historial de 2 cuentas en el
// mismo navegador (ej. una compu compartida del equipo).

const KEY_RECIENTES = (uid: string) => `onehub_recientes_${uid}`;
const KEY_FIJADOS = (uid: string) => `onehub_fijados_${uid}`;
const MAX_RECIENTES = 12;

interface EntradaReciente {
  projectId: string;
  visitadoEn: number; // Date.now()
}

const leer = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

const escribir = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage puede fallar (modo privado, storage lleno) — es solo
    // una conveniencia de UX, nunca debe romper la navegación real.
  }
};

/** Se llama al entrar a un proyecto (ver ProjectContext.tsx) — lo sube al tope, sin duplicar. */
export const registrarProyectoVisitado = (uid: string, projectId: string): void => {
  if (!uid || !projectId) return;
  const actuales = leer<EntradaReciente[]>(KEY_RECIENTES(uid), []);
  const sinEste = actuales.filter((e) => e.projectId !== projectId);
  const actualizados = [{ projectId, visitadoEn: Date.now() }, ...sinEste].slice(0, MAX_RECIENTES);
  escribir(KEY_RECIENTES(uid), actualizados);
};

/** IDs de proyectos recientes, más nuevo primero. */
export const getProyectosRecientes = (uid: string): string[] =>
  leer<EntradaReciente[]>(KEY_RECIENTES(uid), []).map((e) => e.projectId);

export const getProyectosFijados = (uid: string): string[] =>
  leer<string[]>(KEY_FIJADOS(uid), []);

export const esProyectoFijado = (uid: string, projectId: string): boolean =>
  getProyectosFijados(uid).includes(projectId);

/** Alterna fijado/no fijado — devuelve el estado nuevo. */
export const toggleProyectoFijado = (uid: string, projectId: string): boolean => {
  const actuales = getProyectosFijados(uid);
  const yaEsta = actuales.includes(projectId);
  const actualizados = yaEsta ? actuales.filter((id) => id !== projectId) : [projectId, ...actuales];
  escribir(KEY_FIJADOS(uid), actualizados);
  return !yaEsta;
};
